import { v4 } from 'uuid'
import { useHostServices } from '../HostServices'

// istanbul ignore file // TO DO: ignoring this file until we find a solution for tests failing after upgrading to node v18.

/* For further documentation on this hook look at /docs/use-acs-fetch.md */

type JSONResponse<T> = {
  data: T
  error?: {
    errorCode: string
    errorDesciption: string
    errorField: string
  }
}

type AcsFetch = <T>(
  url: string,
  options?: RequestInit,
) => Promise<JSONResponse<T>>

type IAcsFetchOptions = {
  withCif?: boolean
  withGuid?: boolean
  defaultHeaders?: RequestInit['headers']
}

const defaultAPIError = 'Your request could not be processed. Please try again.'

/**
 * Convenience hook for making Apigee API requests. The returned function
 * wraps window.fetch and returns a promise with, minimally, a `data` property
 * defined. If the Apigee request errors but still returns a 200 response with
 * other data, an `error` property will be added to the return value. If the request
 * responds with a 4xx/5xx or returns only an error, it will throw a `new Error` with the
 * Apigee errorCode
 *
 * Request URLs are prefixed with '/acs'.
 *
 * For POST/PATCH/DELETE (any non GET) requests, you may omit
 * the 'Accepts' and 'Content-Type' headers as they're automatically
 * added for you.
 *
 * @param {Object} FetchOptions
 * @param {boolean} FetchOptions.withCif - when true, cif is added to request headers
 * @param {boolean} FetchOptions.withGuid - when true, guid is added to request headers
 * @param {RequestInit} FetchOptions.defaultHeaders - headers that will be passed with every request for this hook instance
 *
 * @example
 * const acsFetch = useAcsFetch({ withGuid: true, withCif: true })
 * const response = await acsFetch("/my-url")
 * if (response.error) {
 *     // handle error
 * }
 * return response.data
 */
export const useAcsFetch = (
  {
    withCif = false,
    withGuid = false,
    defaultHeaders = {},
  } = {} as IAcsFetchOptions,
): AcsFetch => {
  const hostServices = useHostServices()

  const {
    session: { data: sessionData },
  } = hostServices

  const GET_HEADERS: RequestInit['headers'] = {
    Authorization: `Bearer ${sessionData?.access_token}`,
  }

  if (withCif) {
    GET_HEADERS.cif = sessionData?.cif || ''
  }

  if (withGuid) {
    GET_HEADERS.guid = sessionData?.guid || ''
  }

  const UPDATE_HEADERS: RequestInit['headers'] = {
    ...GET_HEADERS,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  const handleResponse = async <T>(
    response: Response,
  ): Promise<JSONResponse<T | null>> => {
    /* 2xx Response */
    if (response.ok) {
      /* Short circuit if no response content */
      if (
        response.headers.get('Content-Length') === '0' ||
        response.status === 204
      ) {
        return { data: null }
      }

      const json = await response.json()
      const { errors, ...restJSON } = json

      if (errors && errors.length > 0) {
        if (Object.keys(json).length === 1) {
          throw new Error(errors[0].errorCode)
        } else {
          return { data: restJSON, error: errors[0] }
        }
      }

      return { data: json }
    }

    /* 4xx/5xx Response */
    const json = await response.json()
    if (json?.errors?.length > 0) {
      throw new Error(json.errors[0].errorCode)
    }

    throw new Error(defaultAPIError)
  }

  return <T>(
    url: string,
    options: RequestInit = { method: 'GET' },
  ): Promise<JSONResponse<T>> => {
    const headers = options.method === 'GET' ? GET_HEADERS : UPDATE_HEADERS
    const { headers: optionalHeaders = {}, ...fetchOptions } = options

    return fetch(`/acs${url}`, {
      headers: {
        ...headers,
        ...defaultHeaders,
        ...optionalHeaders,
        TraceID: v4(),
      },
      ...fetchOptions,
    }).then(handleResponse) as Promise<JSONResponse<T>>
  }
}
